export type CollectionLocale = "en-US" | "ja-JP" | "ko-KR";

const STRINGS: Record<
  CollectionLocale,
  {
    introStandard: string;
    introLibrary: string;
    restoreDraft: string;
    collectionNameLabel: string;
    referenceUrlLabel: string;
    collectionTypeLabel: string;
    collectionTypeStandard: string;
    collectionTypeGraph: string;
    collectionTypeLibrary: string;
    bodyPlaceholder: string;
    addPeoplePlaceholder: string;
    submit: string;
    save: string;
    cancel: string;
    createSuccess: string;
    createError: string;
    updateSuccess: string;
    updateError: string;
    libraryItemsTitle: string;
    libraryItemsSubtitle: string;
    libraryItemsRequired: string;
    addMedia: string;
    addLink: string;
    linkUrlLabel: string;
    linkUrlPlaceholder: string;
    linkResolving: string;
    linkResolveError: string;
    invalidHttpsUrl: string;
    moveUp: string;
    moveDown: string;
    removeItem: string;
    mediaItemLabel: string;
    linkItemLabel: string;
    libraryEmpty: string;
    libraryViewerLoading: string;
    libraryViewerEmpty: string;
    libraryViewerError: string;
    openLink: string;
    collectionKindLabel: string;
    collectionKindStandard: string;
    collectionKindGraph: string;
    collectionKindLibrary: string;
    collectionNotFoundTitle: string;
    collectionNotFoundSubtext: string;
    goHome: string;
  }
> = {
  "en-US": {
    introStandard:
      "Build a collection of related posts, comments, and issues for yourself or with collaborators.",
    introLibrary: "Build a browsable library of media and links in one collection.",
    restoreDraft: "Restore your saved collection draft?",
    collectionNameLabel: "Collection name",
    referenceUrlLabel: "Reference URL",
    collectionTypeLabel: "Collection type",
    collectionTypeStandard: "Standard",
    collectionTypeGraph: "Graph",
    collectionTypeLibrary: "Library",
    bodyPlaceholder: "Body text (optional)",
    addPeoplePlaceholder: "Add people to collection",
    submit: "Submit",
    save: "Save",
    cancel: "Cancel",
    createSuccess: "Collection created successfully",
    createError: "Failed to create collection",
    updateSuccess: "Collection updated successfully",
    updateError: "Failed to update collection",
    libraryItemsTitle: "Library items",
    libraryItemsSubtitle: "Add media or links, then reorder them before publishing.",
    libraryItemsRequired: "Add at least one media item or link to publish a library.",
    addMedia: "Add media",
    addLink: "Add link",
    linkUrlLabel: "Link URL",
    linkUrlPlaceholder: "https://example.com/article",
    linkResolving: "Fetching link preview…",
    linkResolveError: "Could not fetch link metadata right now.",
    invalidHttpsUrl: "Enter a valid https:// URL.",
    moveUp: "Move up",
    moveDown: "Move down",
    removeItem: "Remove item",
    mediaItemLabel: "Media item",
    linkItemLabel: "Link item",
    libraryEmpty: "Your library is empty. Add media or links to get started.",
    libraryViewerLoading: "Loading library items…",
    libraryViewerEmpty: "No library items yet.",
    libraryViewerError: "Could not load this library right now.",
    openLink: "Open link",
    collectionKindLabel: "Collection",
    collectionKindStandard: "Standard",
    collectionKindGraph: "Graph",
    collectionKindLibrary: "Library",
    collectionNotFoundTitle: "Sorry, we couldn't find this collection.",
    collectionNotFoundSubtext: "Double check the URL or try reloading the page.",
    goHome: "Go home",
  },
  "ja-JP": {
    introStandard:
      "関連する投稿・コメント・イシューを、自分用または共同編集でまとめられます。",
    introLibrary: "メディアとリンクをひとつにまとめた閲覧用ライブラリを作成します。",
    restoreDraft: "保存したコレクション下書きを復元しますか？",
    collectionNameLabel: "コレクション名",
    referenceUrlLabel: "参照URL",
    collectionTypeLabel: "コレクション種別",
    collectionTypeStandard: "標準",
    collectionTypeGraph: "グラフ",
    collectionTypeLibrary: "ライブラリ",
    bodyPlaceholder: "本文（任意）",
    addPeoplePlaceholder: "共同編集者を追加",
    submit: "送信",
    save: "保存",
    cancel: "キャンセル",
    createSuccess: "コレクションを作成しました",
    createError: "コレクションを作成できませんでした",
    updateSuccess: "コレクションを更新しました",
    updateError: "コレクションを更新できませんでした",
    libraryItemsTitle: "ライブラリアイテム",
    libraryItemsSubtitle: "メディアやリンクを追加し、公開前に並び順を整えましょう。",
    libraryItemsRequired: "ライブラリを公開するには、少なくとも1件のメディアまたはリンクを追加してください。",
    addMedia: "メディアを追加",
    addLink: "リンクを追加",
    linkUrlLabel: "リンクURL",
    linkUrlPlaceholder: "https://example.com/article",
    linkResolving: "リンクのプレビューを取得中…",
    linkResolveError: "リンク情報を取得できませんでした。",
    invalidHttpsUrl: "有効な https:// URL を入力してください。",
    moveUp: "上へ移動",
    moveDown: "下へ移動",
    removeItem: "項目を削除",
    mediaItemLabel: "メディア項目",
    linkItemLabel: "リンク項目",
    libraryEmpty: "ライブラリはまだ空です。メディアやリンクを追加してください。",
    libraryViewerLoading: "ライブラリアイテムを読み込み中…",
    libraryViewerEmpty: "ライブラリアイテムはまだありません。",
    libraryViewerError: "このライブラリを今は読み込めません。",
    openLink: "リンクを開く",
    collectionKindLabel: "コレクション",
    collectionKindStandard: "標準",
    collectionKindGraph: "グラフ",
    collectionKindLibrary: "ライブラリ",
    collectionNotFoundTitle: "このコレクションは見つかりませんでした。",
    collectionNotFoundSubtext: "URLを確認するか、ページを再読み込みしてください。",
    goHome: "ホームへ戻る",
  },
  "ko-KR": {
    introStandard:
      "관련 게시물, 댓글, 이슈를 나만의 컬렉션이나 협업 컬렉션으로 정리하세요.",
    introLibrary: "미디어와 링크를 한곳에서 둘러볼 수 있는 라이브러리를 만드세요.",
    restoreDraft: "저장된 컬렉션 초안을 복원할까요?",
    collectionNameLabel: "컬렉션 이름",
    referenceUrlLabel: "참조 URL",
    collectionTypeLabel: "컬렉션 유형",
    collectionTypeStandard: "기본",
    collectionTypeGraph: "그래프",
    collectionTypeLibrary: "라이브러리",
    bodyPlaceholder: "본문(선택)",
    addPeoplePlaceholder: "컬렉션에 사람 추가",
    submit: "제출",
    save: "저장",
    cancel: "취소",
    createSuccess: "컬렉션을 만들었습니다",
    createError: "컬렉션을 만들 수 없습니다",
    updateSuccess: "컬렉션을 업데이트했습니다",
    updateError: "컬렉션을 업데이트할 수 없습니다",
    libraryItemsTitle: "라이브러리 항목",
    libraryItemsSubtitle: "미디어나 링크를 추가하고 게시 전에 순서를 정리하세요.",
    libraryItemsRequired: "라이브러리를 게시하려면 미디어나 링크를 최소 1개 추가하세요.",
    addMedia: "미디어 추가",
    addLink: "링크 추가",
    linkUrlLabel: "링크 URL",
    linkUrlPlaceholder: "https://example.com/article",
    linkResolving: "링크 미리보기를 불러오는 중…",
    linkResolveError: "링크 메타데이터를 불러올 수 없습니다.",
    invalidHttpsUrl: "유효한 https:// URL을 입력하세요.",
    moveUp: "위로 이동",
    moveDown: "아래로 이동",
    removeItem: "항목 제거",
    mediaItemLabel: "미디어 항목",
    linkItemLabel: "링크 항목",
    libraryEmpty: "라이브러리가 비어 있습니다. 미디어나 링크를 추가해 보세요.",
    libraryViewerLoading: "라이브러리 항목을 불러오는 중…",
    libraryViewerEmpty: "아직 라이브러리 항목이 없습니다.",
    libraryViewerError: "지금은 이 라이브러리를 불러올 수 없습니다.",
    openLink: "링크 열기",
    collectionKindLabel: "컬렉션",
    collectionKindStandard: "기본",
    collectionKindGraph: "그래프",
    collectionKindLibrary: "라이브러리",
    collectionNotFoundTitle: "이 컬렉션을 찾을 수 없습니다.",
    collectionNotFoundSubtext: "URL을 다시 확인하거나 페이지를 새로고침해 보세요.",
    goHome: "홈으로 이동",
  },
};

export function resolveCollectionLocale(): CollectionLocale {
  if (typeof navigator === "undefined" || !navigator.language) {
    return "en-US";
  }
  const lang = navigator.language.toLowerCase();
  if (lang.startsWith("ja")) {
    return "ja-JP";
  }
  if (lang.startsWith("ko")) {
    return "ko-KR";
  }
  return "en-US";
}

export function getCollectionUiStrings(
  locale?: CollectionLocale
): (typeof STRINGS)["en-US"] {
  const key = locale ?? resolveCollectionLocale();
  return STRINGS[key] ?? STRINGS["en-US"];
}
